import { type AnalysisRun, isRunFinished, type ReplayedRun } from '../model.js';
import { DEFAULT_ENGINE_CATALOGUE, engineLabel } from './engine-rules.js';
import { type LaunchWarning, wipesTranscription } from './launch-options.js';
import { runFailureReason } from './run-failure.js';
import { isLegacyRun } from './run-labels.js';

/**
 * What can be done to a finished run: run it again (GAP-16).
 *
 * A backfilled run offers nothing: a `legacy-*` run stored no job to replay.
 *
 * ## Moving the video's pointer is gone from here
 *
 * A run could be **promoted** — made the one the video shows — and **pinned** — frozen, so a later
 * successful run would not replace it. Both were removed from the back-office on request. A video
 * here has the analyses it has, and each is read on its own terms; there is no official one to
 * choose and nothing to freeze.
 *
 * Virtual Profiler still keeps the pointer, and this repository does not get to decide that: its
 * own frontend sets it, its metrics count it, and the review opened without a run id is still the
 * run upstream considers active. What changed is that nothing here moves it.
 */

export interface LedgerActions {
  replay: boolean;
}

/**
 * Which actions a row of the run history offers.
 *
 * Replay is offered on a finished run that kept its job, which is upstream's own condition: a run
 * from before the holding pen kept none — every real non-legacy run today, 307's included — and a
 * button that always fails after a launch confirmation is worse than no button. It repeats
 * the run exactly — it cannot add coherence to a gestures-only run.
 *
 * Nor is it offered on a run the configuration made fail: the replay resends the configuration
 * snapshot that failed, so it fails again even once the configuration is fixed. The row says to
 * fix the configuration, and a re-analysis takes its current version.
 */
export function ledgerActions(
  run: Pick<
    AnalysisRun,
    'id' | 'trigger' | 'status' | 'resultId' | 'sourceType' | 'hasStoredJob' | 'error'
  >,
): LedgerActions {
  if (isLegacyRun(run)) return { replay: false };

  const failedOnConfiguration = runFailureReason(run.error)?.kind === 'configuration';

  return {
    replay: isRunFinished(run.status) && run.hasStoredJob && !failedOnConfiguration,
  };
}

export interface ReplayConfirmation {
  title: string;
  description: string;
  actionLabel: string;
  /** What has to be read first. A destructive one keeps «Repetir» off until it is ticked. */
  warnings: LaunchWarning[];
}

/**
 * What is asked before replaying a run.
 *
 * A replay is the run again, with what upstream stored: its engines, requested and added, and the
 * promotion its launch options asked for — not the root flag, which upstream does not read here.
 * When that is a promotion without coherence, the video ends with an empty transcription, and that
 * asks for the same tick the re-analysis dialog asks for; there is nothing to switch off here, so
 * the way out it names is a re-analysis.
 */
export function replayConfirmation(
  run: Pick<AnalysisRun, 'enginesRequested' | 'enginesAutoAdded' | 'replayPromotes'>,
  label: string,
): ReplayConfirmation {
  const engines = [...new Set([...run.enginesRequested, ...run.enginesAutoAdded])];
  const named =
    engines.length > 0
      ? engines.map((name) => engineLabel(DEFAULT_ENGINE_CATALOGUE, name)).join(' + ')
      : 'los mismos motores';
  const warnings: LaunchWarning[] = [];

  // An empty list is «not recorded», not «none»: nothing to say it runs without coherence.
  if (engines.length > 0 && wipesTranscription(engines, run.replayPromotes)) {
    warnings.push({
      code: 'wipes_transcription',
      tone: 'destructive',
      title: 'La transcripción del vídeo se quedará vacía',
      text: 'Esta ejecución no pasó por veracidad y, al terminar, sustituirá la transcripción del vídeo por una vacía. Para repetir solo los gestos sin tocarla, reanaliza el vídeo con «Sustituir la transcripción del vídeo» desactivado.',
      needsAcknowledgement: true,
    });
  }

  return {
    title: `¿Repetir el análisis de ${label}?`,
    description: `Se repite con ${named} y la misma configuración. Si termina bien será la última completa del vídeo. ${
      run.replayPromotes
        ? 'Además la app de Virtual Profiler pasará a mostrar la repetición.'
        : 'La app de Virtual Profiler seguirá mostrando el análisis que tiene ahora.'
    }`,
    actionLabel: 'Repetir',
    warnings,
  };
}

export function replayMessage(replayed: ReplayedRun): string {
  return replayed.position === null
    ? 'Repetición en cola'
    : `Repetición en cola (posición ${replayed.position})`;
}

/**
 * The runs of this video that were in flight and no longer are: they landed, one way or another.
 *
 * Read off two consecutive answers of the live-runs query, because that is the only signal this
 * side has — there is no socket on the review screen. A run that landed is when a review can go
 * stale: a promoting one changes which run «la activa» is and rewrites the transcription.
 */
/**
 * Whether any run that landed can have changed which run the video shows.
 *
 * Only a run of the video's own launched to promote can: a group run never promotes, and one
 * launched without promotion stays in the history. It decides how much of a review is asked again
 * — every header of the video when the pointer may have moved, only the landed runs' otherwise —
 * because asking again signs a new video URL and reloads the player.
 */
export function landingMayHavePromoted(
  previous: readonly Pick<AnalysisRun, 'id' | 'promoteOnSuccess' | 'groupId' | 'controlKind'>[],
  landed: readonly string[],
): boolean {
  const ids = new Set(landed);

  // Part of an execution — a group's or a control's — is never the video's own.
  return previous.some(
    (run) =>
      ids.has(run.id) && run.promoteOnSuccess && run.groupId === null && run.controlKind === null,
  );
}

export function runsThatLanded(
  previous: readonly Pick<AnalysisRun, 'id' | 'sourceType' | 'sourceId'>[],
  current: readonly Pick<AnalysisRun, 'id'>[],
  videoId: number,
): string[] {
  const stillLive = new Set(current.map((run) => run.id));

  return previous
    .filter(
      (run) =>
        run.sourceType === 'manual_analysis' &&
        run.sourceId === String(videoId) &&
        !stillLive.has(run.id),
    )
    .map((run) => run.id);
}
